import { useState } from 'react'
import { useHead } from '@unhead/react'
import Header from '@/components/Header'
import ACCalculator from '@/components/ACCalculator'
import HeatPumpCalculator from '@/components/HeatPumpCalculator'
import SEERReferenceChart from '@/components/SEERReferenceChart'
import FAQ from '@/components/FAQ'

type Tab = 'ac' | 'heatpump'

export default function Home() {
  useHead({
    title: 'HVAC SEER Savings Calculator - AC & Heat Pump Efficiency Savings',
    meta: [
      {
        name: 'description',
        content:
          'Calculate your air conditioning and heat pump energy savings with our free SEER & HSPF efficiency calculator. Compare SEER ratings, estimate annual utility savings, and find your payback period.',
      },
      {
        name: 'keywords',
        content:
          'seer rating savings calculator, seer efficiency calculator, seer rating calculator, energy savings calculator hvac, hvac seer calculator, ac seer calculator, heat pump seer calculator, hspf calculator',
      },
      { property: 'og:title', content: 'HVAC SEER Savings Calculator' },
      {
        property: 'og:description',
        content:
          'Free SEER & HSPF efficiency calculator. Compare SEER ratings and estimate your annual utility savings.',
      },
      { property: 'og:type', content: 'website' },
      { name: 'robots', content: 'index, follow' },
    ],
    link: [
      { rel: 'canonical', href: 'https://www.hvacseercalculator.com/' },
    ],
  })

  const [activeTab, setActiveTab] = useState<Tab>('ac')

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 antialiased">
      <Header />

      {/* Hero Section */}
      <section
        id="hero"
        role="region"
        aria-label="HVAC SEER Savings Calculator hero section"
        className="relative overflow-hidden pt-28 pb-16 px-4 sm:px-6"
      >
        {/* Background decoration */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-blue-600/8 rounded-full blur-3xl" />
          <div className="absolute top-20 right-0 w-96 h-96 bg-orange-500/6 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_60%,rgb(2,6,23))]" />
          {/* Subtle grid */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(148,163,184,1) 1px,transparent 1px),linear-gradient(90deg,rgba(148,163,184,1) 1px,transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div
            aria-label="Free online tool"
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-blue-500/25 bg-blue-500/8 text-blue-400 text-xs font-medium tracking-wide mb-6"
          >
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            Free Online Tool &mdash; No Signup Required
          </div>

          {/* H1 with primary keyword */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight mb-5">
            <span className="block">HVAC SEER Savings Calculator</span>
            <span className="block mt-1 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-300 bg-clip-text text-transparent">
              AC &amp; Heat Pump Efficiency Savings
            </span>
          </h1>

          <p className="text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto mb-8">
            Use our free <strong className="text-slate-300">seer rating savings calculator</strong> to estimate how
            much upgrading to a higher-efficiency system will save on your electricity bill — with real-time results,
            CO₂ impact, and ROI payback period.
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            {[
              { icon: '⚡', label: 'Instant results' },
              { icon: '📊', label: 'Live bar charts' },
              { icon: '🌱', label: 'CO₂ footprint' },
              { icon: '💡', label: 'SEER & HSPF' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="flex items-center gap-1.5 text-slate-500"
              >
                <span aria-hidden="true">{stat.icon}</span>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main role="main">
        {/* Calculator Section */}
        <section
          id="calculator"
          role="region"
          aria-labelledby="calc-heading"
          className="scroll-mt-20 px-4 sm:px-6 pb-20"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2
                id="calc-heading"
                className="text-2xl sm:text-3xl font-bold text-white mb-3"
              >
                AC &amp; Heat Pump Efficiency Calculator
              </h2>
              <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
                Adjust the sliders to match your system specs and instantly see your{' '}
                <strong className="text-slate-300">energy savings calculator HVAC</strong> results.
              </p>
            </div>

            {/* Tab selector */}
            <div
              role="tablist"
              aria-label="Calculator type selector"
              className="flex gap-2 mb-0 rounded-t-2xl overflow-hidden border-b-0"
            >
              <button
                role="tab"
                id="tab-ac"
                aria-selected={activeTab === 'ac'}
                aria-controls="panel-ac"
                type="button"
                onClick={() => setActiveTab('ac')}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-semibold rounded-t-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:ring-inset ${
                  activeTab === 'ac'
                    ? 'bg-slate-800/90 text-white border border-b-0 border-slate-700/60'
                    : 'bg-slate-900/40 text-slate-500 hover:text-slate-300 hover:bg-slate-800/40 border border-transparent'
                }`}
              >
                <span aria-hidden="true" className="text-blue-400">❄️</span>
                <span>AC SEER Calculator</span>
                <span
                  aria-hidden="true"
                  className="hidden sm:inline text-xs text-slate-600"
                >
                  (Cooling)
                </span>
              </button>

              <button
                role="tab"
                id="tab-hp"
                aria-selected={activeTab === 'heatpump'}
                aria-controls="panel-hp"
                type="button"
                onClick={() => setActiveTab('heatpump')}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-semibold rounded-t-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:ring-inset ${
                  activeTab === 'heatpump'
                    ? 'bg-slate-800/90 text-white border border-b-0 border-slate-700/60'
                    : 'bg-slate-900/40 text-slate-500 hover:text-slate-300 hover:bg-slate-800/40 border border-transparent'
                }`}
              >
                <span aria-hidden="true" className="text-orange-400">🔥</span>
                <span>Heat Pump HSPF</span>
                <span
                  aria-hidden="true"
                  className="hidden sm:inline text-xs text-slate-600"
                >
                  (Heating)
                </span>
              </button>
            </div>

            {/* Tab panels */}
            <div
              className={`rounded-b-2xl rounded-tr-2xl border border-slate-700/60 overflow-hidden ${
                activeTab === 'ac'
                  ? 'bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/20'
                  : 'bg-gradient-to-br from-slate-900 via-slate-900 to-orange-950/20'
              }`}
            >
              <div
                id="panel-ac"
                role="tabpanel"
                aria-labelledby="tab-ac"
                hidden={activeTab !== 'ac'}
              >
                {activeTab === 'ac' && <ACCalculator />}
              </div>

              <div
                id="panel-hp"
                role="tabpanel"
                aria-labelledby="tab-hp"
                hidden={activeTab !== 'heatpump'}
              >
                {activeTab === 'heatpump' && <HeatPumpCalculator />}
              </div>
            </div>
          </div>
        </section>

        {/* SEER Reference Chart */}
        <section
          id="seer-chart"
          role="region"
          aria-labelledby="chart-heading"
          className="scroll-mt-20 px-4 sm:px-6 pb-20"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2
                id="chart-heading"
                className="text-2xl sm:text-3xl font-bold text-white mb-3"
              >
                Interactive SEER Rating Chart
              </h2>
              <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
                See how operating costs drop as SEER efficiency increases for a standard 3-ton AC unit.
                Our <strong className="text-slate-300">seer efficiency calculator</strong> reference
                shows why every SEER point matters.
              </p>
            </div>
            <SEERReferenceChart />
          </div>
        </section>

        {/* FAQ Section */}
        <section
          id="faq-section"
          role="region"
          aria-label="Frequently asked questions"
          className="scroll-mt-20 px-4 sm:px-6 pb-24"
        >
          <div className="max-w-3xl mx-auto">
            <FAQ />
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer
        role="contentinfo"
        className="border-t border-slate-800/50 px-4 sm:px-6 py-10"
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span aria-hidden="true" className="text-xl">🌡️</span>
              <div>
                <p className="text-sm font-bold text-white">HVAC SEER Savings</p>
                <p className="text-xs text-slate-600">hvacseercalculator.com</p>
              </div>
            </div>

            <nav aria-label="Footer navigation" className="flex gap-5 text-xs text-slate-500">
              <a href="#calculator" className="hover:text-slate-300 transition-colors">Calculator</a>
              <a href="#seer-chart" className="hover:text-slate-300 transition-colors">SEER Chart</a>
              <a href="#faq" className="hover:text-slate-300 transition-colors">FAQ</a>
            </nav>

            <p className="text-xs text-slate-600 text-center sm:text-right">
              Estimates are for informational purposes only.
              <br />
              Results may vary based on climate and usage.
            </p>
          </div>
        </div>
      </footer>

      {/* JSON-LD structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'HVAC SEER Savings Calculator',
            url: 'https://www.hvacseercalculator.com',
            description:
              'Free SEER and HSPF efficiency calculator. Compare SEER ratings, estimate annual utility savings, and find your payback period for AC and heat pump upgrades.',
            applicationCategory: 'UtilityApplication',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          }),
        }}
      />
    </div>
  )
}
